import { ResponsiveKpis } from '@/components/responsive-kpis';
import { PageActions } from '@/components/page-actions';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { useEffect, useMemo } from 'react';
import { ScrollTable, useLinkedScroll } from '@/components/scroll-table';
import { TabStrip } from '@/components/tab-strip';
import { Link } from '@tanstack/react-router';
import {
  useCampaigns,
  useDeliveryPlacements,
  useProductions,
  useReportingByAudience,
  useReportingQuery,
} from '@/api/hooks';
import { useCan } from '@/auth/capabilities';
import { DateFilter } from '@/components/dropdowns';
import { QueryErrorNote } from '@/components/error-panel';
import { Icons, fmt, MoneyCell } from '@/components/primitives';
import { ReportingFreshnessWarnings } from '@/components/reporting-freshness';
import { ReportingDataControls } from '@/components/reporting-controls';
import {
  ScopeSearchPicker,
  renderCampaignRow,
  renderProductionRow,
} from '@/components/scope-search-picker';
import { csvFilename, downloadCsv } from '@/lib/csv';
import { aggregatePlacements } from '@/lib/delivery-metrics';
import { fmtDateInput, nyDateInputToMs } from '@/lib/datetime';
import {
  deliveryPlacementRequest,
  operationalReportDateQuery,
  operationalFreshnessRequest,
  serializeOperationalReportSearch,
  updateDeliveryReportSearch,
} from '@/lib/operational-report-search';
import type {
  DeliveryReportSearch,
  DeliveryReportSearchPatch,
  DeliveryReportTab,
} from '@/lib/operational-report-search';
import {
  reportMetricIsAvailable,
  reportMetricUnavailableReason,
  reportingMetricAvailability,
} from '@/lib/reporting-availability';

interface DateState {
  preset: string;
  start: Date;
  end: Date;
}

interface DeliveryReportsProps {
  search: DeliveryReportSearch;
  onSearchChange: (
    patch: DeliveryReportSearchPatch,
    options?: { replace?: boolean },
  ) => void;
}

const CHANNEL_META: Record<string, { label: string; accent: string }> = {
  meta: { label: 'Meta', accent: 'oklch(0.60 0.18 250)' },
  ctv: { label: 'CTV', accent: 'oklch(0.60 0.18 320)' },
  dsp: { label: 'Display', accent: 'oklch(0.60 0.16 145)' },
};

const AUDIENCE_TYPES = [
  { id: 'retargeting', name: 'Retargeting' },
  { id: 'direct', name: 'Direct' },
  { id: 'lookalike', name: 'Lookalike' },
] as const;

const DELIVERY_TABS: Array<{ id: DeliveryReportTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'campaign', label: 'By Campaign' },
  { id: 'production', label: 'By Assigned Production' },
  { id: 'channel', label: 'By Channel' },
  { id: 'audience', label: 'By Audience' },
  { id: 'audienceType', label: 'By Audience Type' },
];

function DeliveryReports({ search, onSearchChange }: DeliveryReportsProps) {
  const canExport = useCan('reports_export');
  const tab = search.tab;
  const scopeId = search.scopeId ?? null;
  const channel = search.channel;
  const date = useMemo<DateState>(() => ({
    preset: 'custom',
    start: new Date(nyDateInputToMs(search.from)),
    end: new Date(nyDateInputToMs(search.to)),
  }), [search.from, search.to]);

  const campaignsQuery = useCampaigns();
  const productionsQuery = useProductions();
  const campaigns = campaignsQuery.data ?? [];
  const productions = productionsQuery.data ?? [];
  const dateQuery = useMemo(() => operationalReportDateQuery(search), [search]);
  const audienceOptionsQuery = useReportingByAudience(dateQuery);
  const audienceOptions = audienceOptionsQuery.data ?? [];
  const productionMap = useMemo(
    () => new Map(productions.map((production) => [production.id as string, production])),
    [productions],
  );

  useEffect(() => {
    if (tab === 'campaign') {
      const next = scopeId && campaigns.some((row) => row.id === scopeId)
        ? scopeId
        : campaigns[0]?.id;
      if (next !== scopeId) onSearchChange({ scopeId: next }, { replace: true });
    } else if (tab === 'production') {
      const next = scopeId && productions.some((row) => row.id === scopeId)
        ? scopeId
        : productions[0]?.id;
      if (next !== scopeId) onSearchChange({ scopeId: next }, { replace: true });
    } else if (tab === 'audience') {
      const keys = audienceOptions.map(
        (row) => `${row.audience_id}:${row.audience_revision}`,
      );
      const next = scopeId && keys.includes(scopeId) ? scopeId : keys[0];
      if (next !== scopeId) onSearchChange({ scopeId: next }, { replace: true });
    } else if (tab === 'audienceType') {
      const next = AUDIENCE_TYPES.some((row) => row.id === scopeId)
        ? scopeId ?? undefined
        : AUDIENCE_TYPES[0].id;
      if (next !== scopeId) onSearchChange({ scopeId: next }, { replace: true });
    } else if (scopeId !== null) {
      onSearchChange({ scopeId: undefined }, { replace: true });
    }
  }, [audienceOptions, campaigns, onSearchChange, productions, scopeId, tab]);

  const placementsQuery = useDeliveryPlacements(deliveryPlacementRequest(search));
  const freshnessRequest = useMemo(() => operationalFreshnessRequest(search), [search]);
  const freshnessQuery = useReportingQuery(freshnessRequest);
  const availability = reportingMetricAvailability(
    'delivery',
    freshnessQuery.data?.freshness,
  );
  const deliveryAvailable = reportMetricIsAvailable(availability, 'delivery');
  const deliveryReason = reportMetricUnavailableReason(availability, 'delivery');
  const rows = placementsQuery.data ?? [];

  // The API returns additive placement facts. Aggregate their complete
  // response into the canonical one-row-per-placement view and derive VCR
  // only after summing its true numerator and denominator. Canonical
  // publication metadata is authoritative: legacy rows stay hidden until a
  // delivery publication covers the requested scope.
  // Aggregate whatever the API returned. It no longer withholds a day that the
  // provider can still restate, so these rows are real measurements even when
  // they are not yet final — blanking them here turned "still settling" into a
  // table of zeroes.
  const placements = useMemo(() => aggregatePlacements(rows), [rows]);
  // True when something in view can still move, so the totals can say so
  // instead of implying they are settled.
  const hasProvisional = placements.some((row) => row.is_complete === false);

  const totalImpressions = placements.reduce((sum, row) => sum + row.impressions, 0);
  const totalSpend = placements.reduce((sum, row) => sum + row.spend_cents / 100, 0);
  const totalClicks = placements.reduce((sum, row) => sum + row.clicks, 0);
  const totalVideoStarts = placements.reduce((sum, row) => sum + row.video_starts, 0);
  const totalVideoCompletions = placements.reduce(
    (sum, row) => sum + row.video_completions,
    0,
  );
  const totalVcr = totalVideoStarts ? totalVideoCompletions / totalVideoStarts : null;
  const hasCtvOnly = placements.length > 0 && placements.every((row) => row.channel === 'ctv');

  const exportRows = useMemo(
    () => placements.map((row) => ({
      Placement: row.placement,
      Channel: row.channel,
      Impressions: row.impressions,
      Clicks: row.clicks,
      'Spend (cents)': row.spend_cents,
      'VCR (%)': row.vcr === null ? null : row.vcr * 100,
      'CTR (%)': row.impressions ? row.clicks / row.impressions * 100 : 0,
      'CPM (dollars)': row.impressions ? row.spend_cents / 100 / row.impressions * 1000 : 0,
      'Placement attribution': 'Unavailable: attributed sales have no placement dimension',
    })),
    [placements],
  );

  const linked = useLinkedScroll();
  const colGroup = (
    <colgroup>
      <col/>
      {tab !== 'channel' && <col style={{ width: 140 }}/>}
      <col style={{ width: 140 }}/>
      <col style={{ width: 160 }}/>
      {!hasCtvOnly && <col style={{ width: 100 }}/>}
      <col style={{ width: 90 }}/>
      <col style={{ width: 120 }}/>
      <col style={{ width: 90 }}/>
      <col style={{ width: 110 }}/>
      <col style={{ width: 90 }}/>
    </colgroup>
  );

  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div><h1 className="page-title">Delivery</h1></div>
        <PageActions>
          <ReportingDataControls
            data-phone="meta"
            freshness={freshnessQuery.data?.freshness}
            dataset="delivery"
          />
          <DateFilter
            value={date}
            onChange={(next: { start: Date; end: Date }) => onSearchChange({
              from: fmtDateInput(next.start),
              to: fmtDateInput(next.end),
            })}
          />
          <button
            type="button"
            className="btn btn-icon-phone"
            data-phone="inline"
            aria-label="Export CSV"
            disabled={!canExport || exportRows.length === 0}
            onClick={() => downloadCsv(csvFilename(`delivery-${tab.toLowerCase()}`), exportRows)}
            title={!canExport
              ? 'You do not have permission to export reports'
              : !deliveryAvailable
                ? deliveryReason
                : 'Export the active filtered view'}
          >
            <span>{Icons.dl}</span><span className="phone-long">Export</span>
          </button>
        </PageActions>
      </div>

      <div style={{ padding: '0 18px', borderBottom: '1px solid var(--border, var(--line))' }}>
        <TabStrip className="report-tabs" role="tablist" aria-label="Delivery report views">
          {DELIVERY_TABS.map((item) => (
            <Link
              to="/reports/delivery"
              search={serializeOperationalReportSearch(
                updateDeliveryReportSearch(search, { tab: item.id, scopeId: undefined }),
              )}
              preload="intent"
              role="tab"
              aria-selected={tab === item.id}
              key={item.id}
              className={`report-tab ${tab === item.id ? 'on' : ''}`}
              onKeyDown={(event) => {
                if (event.key !== ' ') return;
                event.preventDefault();
                event.currentTarget.click();
              }}
            >
              {item.label}
            </Link>
          ))}
        </TabStrip>
      </div>

      {tab === 'campaign' && (
        <ResponsiveFilters className="filter-bar" style={{ justifyContent: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
          <FilterLabel>Campaign</FilterLabel>
          <ScopeSearchPicker
            items={campaigns}
            value={scopeId}
            onChange={(next) => onSearchChange({ scopeId: next ?? undefined })}
            placeholder={campaignsQuery.isLoading ? 'Loading campaigns…' : 'Select campaign…'}
            searchPlaceholder="Search campaigns or productions…"
            getKey={(campaign) => campaign.id}
            getSearchText={(campaign) => `${campaign.name} ${productionMap.get(campaign.production_id)?.name ?? ''}`}
            renderRow={(campaign) => renderCampaignRow(campaign, productionMap)}
          />
        </ResponsiveFilters>
      )}
      {tab === 'production' && (
        <ResponsiveFilters className="filter-bar" style={{ justifyContent: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
          <FilterLabel>Assigned production</FilterLabel>
          <ScopeSearchPicker
            items={productions}
            value={scopeId}
            onChange={(next) => onSearchChange({ scopeId: next ?? undefined })}
            placeholder={productionsQuery.isLoading ? 'Loading productions…' : 'Select production…'}
            searchPlaceholder="Search productions…"
            getKey={(production) => production.id}
            getSearchText={(production) => production.name}
            renderRow={renderProductionRow}
          />
        </ResponsiveFilters>
      )}
      {tab === 'channel' && (
        <ResponsiveFilters className="filter-bar" style={{ justifyContent: 'flex-start', gap: 6 }}>
          <FilterLabel>Channel</FilterLabel>
          {Object.entries(CHANNEL_META).map(([id, metadata]) => (
            <button
              type="button"
              key={id}
              className={`btn btn-sm ${channel === id ? 'btn-primary' : ''}`}
              onClick={() => onSearchChange({ channel: id as DeliveryReportSearch['channel'] })}
              style={{ minWidth: 90 }}
            >
              {metadata.label}
            </button>
          ))}
        </ResponsiveFilters>
      )}
      {tab === 'audience' && (
        <ResponsiveFilters className="filter-bar" style={{ justifyContent: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
          <FilterLabel>Audience</FilterLabel>
          <ScopeSearchPicker
            items={audienceOptions}
            value={scopeId}
            onChange={(next) => onSearchChange({ scopeId: next ?? undefined })}
            placeholder={audienceOptionsQuery.isLoading ? 'Loading audiences…' : 'Select audience revision…'}
            searchPlaceholder="Search audience revisions…"
            getKey={(audience) => `${audience.audience_id}:${audience.audience_revision}`}
            getSearchText={(audience) => `${audience.name} ${audience.audience_type} revision ${audience.audience_revision}`}
            renderRow={(audience) => (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {audience.name || audience.audience_id}
                <span style={{ color: 'var(--ink-3)' }}>
                  {' '}— {audience.audience_type} · r{audience.audience_revision}
                </span>
              </span>
            )}
          />
        </ResponsiveFilters>
      )}
      {tab === 'audienceType' && (
        <ResponsiveFilters className="filter-bar" style={{ justifyContent: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
          <FilterLabel>Audience type</FilterLabel>
          {AUDIENCE_TYPES.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`btn btn-sm ${scopeId === item.id ? 'btn-primary' : ''}`}
              onClick={() => onSearchChange({ scopeId: item.id })}
              style={{ minWidth: 90 }}
            >
              {item.name}
            </button>
          ))}
        </ResponsiveFilters>
      )}

      <ResponsiveKpis><div className="kpi-strip" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Impressions</div>
          <div className="kpi-val">
            {placementsQuery.isLoading || freshnessQuery.isLoading
              ? '…'
              : deliveryAvailable ? fmt.nKpi(totalImpressions) : '—'}
          </div>
          <div className="kpi-delta">
            <span className="lbl" title={!deliveryAvailable ? deliveryReason : undefined}>
              {deliveryAvailable
                ? `${placements.length} placements${hasProvisional ? ' · provisional' : ''}`
                : 'Delivery unavailable'}
            </span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Spend</div>
          <div className="kpi-val">
            {placementsQuery.isLoading || freshnessQuery.isLoading
              ? '…'
              : deliveryAvailable ? <MoneyCell value={totalSpend} dp={2}/> : '—'}
          </div>
          <div className="kpi-delta">
            <span className="lbl" title={!deliveryAvailable ? deliveryReason : undefined}>
              {deliveryAvailable
                ? `CPM $${totalImpressions ? (totalSpend / totalImpressions * 1000).toFixed(2) : '0.00'}`
                : 'No published delivery snapshot'}
            </span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Clicks</div>
          <div className="kpi-val">
            {placementsQuery.isLoading || freshnessQuery.isLoading
              ? '…'
              : deliveryAvailable ? fmt.nKpi(totalClicks) : '—'}
          </div>
          <div className="kpi-delta">
            <span className="lbl" title={!deliveryAvailable ? deliveryReason : undefined}>
              {deliveryAvailable
                ? `CTR ${totalImpressions ? (totalClicks / totalImpressions * 100).toFixed(2) : '0.00'}%`
                : 'Delivery unavailable'}
            </span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Placement Attribution</div>
          <div className="kpi-val">{placementsQuery.isLoading ? '…' : '—'}</div>
          <div className="kpi-delta"><span className="lbl">Not allocated across placements</span></div>
        </div>
      </div></ResponsiveKpis>

      <ReportingFreshnessWarnings
        freshness={freshnessQuery.data?.freshness}
        dataset="delivery"
      />
      {freshnessQuery.isError && (
        <div style={{ padding: '12px 18px 0' }}>
          <QueryErrorNote
            error={freshnessQuery.error}
            what="delivery freshness"
            onRetry={() => void freshnessQuery.refetch()}
          />
        </div>
      )}

      <ScrollTable className="tbl-wrap delivery-tbl-wrap" label="Delivery metrics" identityColumn={1} scrollRef={linked.primary.ref} onScroll={linked.primary.onScroll}>
        <table className="tbl delivery-main-tbl">
          {colGroup}
          <thead>
            <tr>
              <th style={{ minWidth: 240 }}>Placement</th>
              {tab !== 'channel' && <th>Channel</th>}
              <th className="num">Impressions</th>
              <th className="num">Share</th>
              {!hasCtvOnly && <th className="num">Clicks</th>}
              {!hasCtvOnly && <th className="num">CTR</th>}
              {hasCtvOnly && <th className="num">VCR</th>}
              <th className="num">Spend</th>
              <th className="num">CPM</th>
              <th className="num">Attributed conversions</th>
              <th className="num">CPA</th>
            </tr>
          </thead>
          <tbody>
            {placementsQuery.isError && placements.length === 0 && (
              <tr>
                <td colSpan={10}>
                  <QueryErrorNote
                    error={placementsQuery.error}
                    what="delivery placements"
                    onRetry={() => void placementsQuery.refetch()}
                  />
                </td>
              </tr>
            )}
            {placementsQuery.isLoading && placements.length === 0 && (
              <tr><td colSpan={10} className="muted" style={{ textAlign: 'center', padding: 18 }}>Loading…</td></tr>
            )}
            {!placementsQuery.isLoading && !placementsQuery.isError && !deliveryAvailable && (
              <tr>
                <td colSpan={10} className="muted" style={{ textAlign: 'center', padding: 18 }}>
                  {deliveryReason} Delivery metrics are unavailable, not confirmed zero.
                </td>
              </tr>
            )}
            {!placementsQuery.isLoading && !placementsQuery.isError && deliveryAvailable && placements.length === 0 && (
              <tr><td colSpan={10} className="muted" style={{ textAlign: 'center', padding: 18 }}>No placement data in the selected window.</td></tr>
            )}
            {placements.map((row) => {
              const metadata = CHANNEL_META[row.channel] ?? { label: row.channel, accent: 'var(--ink-3)' };
              const ctr = row.impressions ? row.clicks / row.impressions * 100 : 0;
              const spend = row.spend_cents / 100;
              const share = totalImpressions ? row.impressions / totalImpressions * 100 : 0;
              return (
                <tr key={`${row.channel}|${row.placement}`}>
                  <td className="name">{row.placement}</td>
                  {tab !== 'channel' && (
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: metadata.accent, display: 'inline-block' }}/>
                        {metadata.label}
                      </span>
                    </td>
                  )}
                  <td className="num">{row.impressions.toLocaleString()}</td>
                  <td className="num">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                      <div style={{ width: 48, height: 5, background: 'var(--bg-sunk)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ width: `${share}%`, height: '100%', background: metadata.accent }}/>
                      </div>
                      <span style={{ minWidth: 36 }}>{share.toFixed(1)}%</span>
                    </div>
                  </td>
                  {!hasCtvOnly && <td className="num">{row.clicks ? row.clicks.toLocaleString() : '—'}</td>}
                  {!hasCtvOnly && <td className="num">{row.clicks ? `${ctr.toFixed(2)}%` : '—'}</td>}
                  {hasCtvOnly && <td className="num"><span className={row.vcr !== null && row.vcr >= 0.95 ? 'up' : ''}>{row.vcr !== null ? `${(row.vcr * 100).toFixed(1)}%` : '—'}</span></td>}
                  <td className="num"><MoneyCell value={spend}/></td>
                  <td className="num">{row.impressions ? <MoneyCell value={spend / row.impressions * 1000} dp={2}/> : '$0.00'}</td>
                  <td className="num" title="Attributed sales do not contain a placement dimension">—</td>
                  <td className="num" title="CPA cannot be allocated honestly without placement-level attribution">—</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollTable>

      {placements.length > 0 && (
        <ScrollTable className="delivery-total-bar" label="Delivery totals" identityColumn={1} scrollRef={linked.secondary.ref} onScroll={linked.secondary.onScroll}>
          <table className="tbl delivery-total-tbl">
            {colGroup}
            <tbody>
              <tr className="delivery-total">
                <td
                  title={hasProvisional
                    ? 'Includes days the provider can still restate — today’s delivery, or a day inside its restatement window. These are measurements, not estimates, but they can still move.'
                    : undefined}
                >
                  Total · {placements.length} placements
                  {hasProvisional && <span className="muted"> · provisional</span>}
                </td>
                {tab !== 'channel' && <td className="muted">—</td>}
                <td className="num">{totalImpressions.toLocaleString()}</td>
                <td className="num">100.0%</td>
                {!hasCtvOnly && <td className="num">{totalClicks.toLocaleString()}</td>}
                {!hasCtvOnly && <td className="num">{totalImpressions ? `${(totalClicks / totalImpressions * 100).toFixed(2)}%` : '—'}</td>}
                {hasCtvOnly && <td className="num">{totalVcr === null ? '—' : `${(totalVcr * 100).toFixed(1)}%`}</td>}
                <td className="num"><MoneyCell value={totalSpend}/></td>
                <td className="num"><MoneyCell value={totalImpressions ? totalSpend / totalImpressions * 1000 : 0} dp={2}/></td>
                <td className="num">—</td>
                <td className="num">—</td>
              </tr>
            </tbody>
          </table>
        </ScrollTable>
      )}
    </div>
  );
}

function FilterLabel({ children }: { children: string }) {
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink-3)', marginRight: 6 }}>
      {children}
    </span>
  );
}

export { DeliveryReports };
